import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  useMediaQuery,
  useTheme,
  Tab,
  Tabs,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Zoom,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  Email,
  Search,
  Dashboard,
  People,
  Settings,
  Analytics,
  Speed,
  Security,
  TrendingUp,
  CheckCircle,
  Inbox,
  Campaign,
  PersonAdd,
  Send,
  Schedule,
  FilterAlt,
  LocationOn,
  LocalShipping,
  Verified,
  Article,
  Assignment,
  GroupWork,
  AdminPanelSettings,
  MailOutline,
  AttachMoney,
  WbSunny,
  Brightness4,
} from '@mui/icons-material';

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const NewFeaturesSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState(0);

  const primaryGradient = isDark
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)';

  const sectionBg = isDark
    ? alpha('#0f172a', 0.95)
    : 'linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%)';

  const cardBg = isDark ? alpha('#1e293b', 0.6) : alpha('#ffffff', 0.95);
  const textColor = isDark ? '#f1f5f9' : '#0f172a';
  const accentColor = isDark ? '#818cf8' : '#3b82f6';

  const tabCategories = [
    { label: 'Email Studio', icon: <Email /> },
    { label: 'Carrier Search', icon: <Search /> },
    { label: 'Analytics & Tracking', icon: <Analytics /> },
  ];

  const featuresByCategory = {
    0: { // Email Studio
      title: 'Complete Email Marketing Suite',
      subtitle: 'Send, manage, and optimize your email campaigns with powerful tools',
      features: [
        {
          icon: <MailOutline />,
          title: 'Email Accounts Management',
          description: 'Add and manage multiple email accounts from Gmail, Outlook, Yahoo, and custom SMTP providers. Switch between accounts seamlessly.',
          highlights: ['Multi-account support', 'SMTP/IMAP configuration', 'Account health monitoring'],
          color: '#3b82f6',
        },
        {
          icon: <Campaign />,
          title: 'Email Campaigns',
          description: 'Create and send bulk email campaigns with personalized merge tags. Track opens, clicks, and responses in real-time.',
          highlights: ['Bulk email sending', 'Merge tag personalization', 'Campaign scheduling', 'A/B testing'],
          color: '#8b5cf6',
        },
        {
          icon: <Send />,
          title: 'Quick Compose',
          description: 'Send individual emails quickly with rich text editing, attachments, and template support.',
          highlights: ['Rich text editor', 'File attachments', 'Email templates', 'Draft saving'],
          color: '#ec4899',
        },
        {
          icon: <Inbox />,
          title: 'Unified Inbox',
          description: 'See replies and incoming emails from all connected accounts in one unified inbox. Review conversations, reply faster, and manage every account from a single screen.',
          highlights: ['All accounts in one inbox', 'Real-time replies', 'Smart filters', 'Clean conversation threads'],
          color: '#06b6d4',
        },
        {
          icon: <Article />,
          title: 'Email Templates',
          description: 'Create, save, and reuse professional email templates. Build your template library for faster outreach.',
          highlights: ['Template builder', 'Variable placeholders', 'Category organization', 'Quick insert'],
          color: '#10b981',
        },
        {
          icon: <WbSunny />,
          title: 'Email Warmup',
          description: 'Automatically warm up new email accounts to improve deliverability and avoid spam folders.',
          highlights: ['Automated warmup', 'Gradual sending increase', 'Reputation building', 'Progress tracking'],
          color: '#f59e0b',
        },
      ],
    },
    1: { // Carrier Search
      title: 'Advanced Carrier Intelligence',
      subtitle: 'Find, verify, and connect with carriers using powerful search capabilities',
      features: [
        {
          icon: <Search />,
          title: 'MC Number Search',
          description: 'Search carriers by MC number with instant FMCSA data retrieval. Get complete carrier profiles in seconds.',
          highlights: ['FMCSA integration', 'Real-time data', 'Instant verification', 'Detailed profiles'],
          color: '#3b82f6',
        },
        {
          icon: <FilterAlt />,
          title: 'MC Range Search',
          description: 'Search carriers within MC number ranges automatically. Perfect for prospecting new carriers.',
          highlights: ['Range queries', 'Auto-search', 'Bulk data retrieval', 'Smart filtering'],
          color: '#8b5cf6',
        },
        {
          icon: <LocationOn />,
          title: 'Location-Based Search',
          description: 'Find carriers by state, city, or region. Filter by operating authority and service areas.',
          highlights: ['State filtering', 'City search', 'Geographic targeting', 'Service area mapping'],
          color: '#ec4899',
        },
        {
          icon: <LocalShipping />,
          title: 'Equipment Type Filtering',
          description: 'Filter carriers by equipment type, truck count, and hauling capabilities.',
          highlights: ['Equipment filters', 'Fleet size', 'Truck types', 'Specialty equipment'],
          color: '#06b6d4',
        },
        {
          icon: <Verified />,
          title: 'Authority Verification',
          description: 'Verify carrier operating authority, insurance status, and safety ratings instantly.',
          highlights: ['Authority check', 'Insurance verification', 'Safety ratings', 'Compliance status'],
          color: '#10b981',
        },
        {
          icon: <Assignment />,
          title: 'Carrier Profiles',
          description: 'Access comprehensive carrier profiles with contact info, operating history, and performance data.',
          highlights: ['Full profiles', 'Contact details', 'Operating history', 'Notes & tags'],
          color: '#f59e0b',
        },
      ],
    },
    2: { // Analytics & Tracking
      title: 'Real-Time Analytics & Performance Tracking',
      subtitle: 'Monitor every activity, track performance, and make data-driven decisions',
      features: [
        {
          icon: <Dashboard />,
          title: 'Activity Dashboard',
          description: 'Real-time dashboard showing all user activities, email sends, searches, and system events.',
          highlights: ['Live activity feed', 'User actions', 'System events', 'Time tracking'],
          color: '#3b82f6',
        },
        {
          icon: <Analytics />,
          title: 'Email Analytics',
          description: 'Track email open rates, click rates, bounce rates, and response rates for every campaign.',
          highlights: ['Open tracking', 'Click tracking', 'Bounce monitoring', 'Response rates'],
          color: '#8b5cf6',
        },
        {
          icon: <TrendingUp />,
          title: 'Performance Metrics',
          description: 'Comprehensive metrics showing team performance, productivity, and campaign effectiveness.',
          highlights: ['Team stats', 'Productivity metrics', 'Campaign ROI', 'Conversion tracking'],
          color: '#ec4899',
        },
        {
          icon: <Schedule />,
          title: 'Daily Activity Reports',
          description: 'Automated daily reports showing employee activities, emails sent, and hours worked.',
          highlights: ['Daily summaries', 'Employee tracking', 'Time logs', 'Export reports'],
          color: '#06b6d4',
        },
        {
          icon: <Assignment />,
          title: 'Audit Logs',
          description: 'Complete audit trail of all system activities for compliance and security monitoring.',
          highlights: ['Full audit trail', 'User actions', 'Security logs', 'Compliance ready'],
          color: '#10b981',
        },
        {
          icon: <Speed />,
          title: 'Real-Time Notifications',
          description: 'Instant notifications for important events, email responses, and system alerts.',
          highlights: ['Push notifications', 'Email alerts', 'Custom triggers', 'Priority alerts'],
          color: '#f59e0b',
        },
      ],
    },
  };

  const currentCategory = featuresByCategory[activeTab];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: sectionBg,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decoration */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.1 : 0.05,
          background: `
            radial-gradient(circle at 10% 20%, ${alpha(accentColor, 0.3)} 0%, transparent 50%),
            radial-gradient(circle at 90% 80%, ${alpha('#8b5cf6', 0.3)} 0%, transparent 50%)
          `,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Stack spacing={2} sx={{ mb: 6, textAlign: 'center' }}>
          <Chip
            label="Platform Features"
            sx={{
              background: primaryGradient,
              color: '#fff',
              fontWeight: 700,
              alignSelf: 'center',
              px: 2,
              py: 2.5,
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            sx={{
              fontFamily: '"Sora", "Inter", sans-serif',
              fontWeight: 900,
              color: textColor,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Everything You Need to Succeed
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6),
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.8,
            }}
          >
            From email campaigns to carrier intelligence, our platform provides all the tools
            your team needs to scale operations and drive results.
          </Typography>
        </Stack>

        {/* Category Tabs */}
        <Box sx={{ mb: 6 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            centered={!isMobile}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                minHeight: 60,
                color: isDark ? alpha(textColor, 0.6) : alpha(textColor, 0.5),
                transition: 'all 0.3s',
                '&.Mui-selected': {
                  color: accentColor,
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                background: primaryGradient,
                borderRadius: 2,
              },
            }}
          >
            {tabCategories.map((category, idx) => (
              <Tab
                key={idx}
                icon={category.icon}
                label={category.label}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        {/* Category Content */}
        <Fade in key={activeTab} timeout={600}>
          <Box>
            <Stack spacing={3} sx={{ mb: 6, textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: textColor,
                  fontFamily: '"Sora", "Inter", sans-serif',
                }}
              >
                {currentCategory.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6),
                  maxWidth: 700,
                  mx: 'auto',
                  fontSize: '1.1rem',
                }}
              >
                {currentCategory.subtitle}
              </Typography>
            </Stack>

            {/* Features Grid */}
            <Grid container spacing={4}>
              {currentCategory.features.map((feature, idx) => (
                <Grid item xs={12} md={6} lg={4} key={idx}>
                  <Zoom in timeout={200 + idx * 100}>
                    <Card
                      sx={{
                        height: '100%',
                        background: cardBg,
                        backdropFilter: 'blur(20px)',
                        borderRadius: 3,
                        border: `1px solid ${alpha(feature.color, isDark ? 0.2 : 0.15)}`,
                        boxShadow: `0 8px 32px ${alpha('#000', isDark ? 0.3 : 0.08)}`,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-12px)',
                          boxShadow: `0 20px 60px ${alpha(feature.color, 0.25)}`,
                          '& .feature-icon': {
                            transform: 'scale(1.1) rotate(5deg)',
                            background: `linear-gradient(135deg, ${feature.color} 0%, ${alpha(feature.color, 0.7)} 100%)`,
                          },
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: `linear-gradient(90deg, transparent, ${alpha(feature.color, 0.1)}, transparent)`,
                          animation: `${shimmer} 3s infinite`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Stack spacing={3}>
                          {/* Icon */}
                          <Box
                            className="feature-icon"
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: 3,
                              background: alpha(feature.color, isDark ? 0.2 : 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.4s',
                              boxShadow: `0 4px 20px ${alpha(feature.color, 0.2)}`,
                            }}
                          >
                            {React.cloneElement(feature.icon, {
                              sx: { fontSize: 32, color: feature.color },
                            })}
                          </Box>

                          {/* Title */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: textColor,
                              fontFamily: '"Sora", "Inter", sans-serif',
                            }}
                          >
                            {feature.title}
                          </Typography>

                          {/* Description */}
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6),
                              lineHeight: 1.7,
                            }}
                          >
                            {feature.description}
                          </Typography>

                          {/* Highlights */}
                          <List dense sx={{ p: 0 }}>
                            {feature.highlights.map((highlight, i) => (
                              <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckCircle
                                    sx={{
                                      fontSize: 18,
                                      color: feature.color,
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={highlight}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    sx: {
                                      color: isDark ? alpha(textColor, 0.8) : alpha(textColor, 0.7),
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default NewFeaturesSection;
